import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { currencyFormatter } from '../utils/reportingUtils';
import { buildLineItemsFilterGroups } from '../constants/lineItemsFiltersConfig';
import { SORT_TYPES } from '../constants/lineItemsColumnsConfig';

const axios = require('axios').default;

const LINE_ITEMS_COLUMNS = [
  { id: 'event_id', label: 'Event ID', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'number' },
  { id: 'event_date', label: 'Event Date', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'date' },
  { id: 'event_name', label: 'Event Name', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'venue_name', label: 'Venue Name', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'finance_name', label: 'Finance Name', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'estimate', label: 'Estimate', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'number' },
  { id: 'actual', label: 'Actual', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'number' },
  { id: 'paid', label: 'Paid', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
  { id: 'type', label: 'Type', alwaysVisible: false, defaultVisible: true, sortable: true, sortType: 'text' },
];

const getDefaultVisibleColumns = () =>
  LINE_ITEMS_COLUMNS.filter(c => c.defaultVisible);

const getDefaultHiddenColumns = () =>
  LINE_ITEMS_COLUMNS.filter(c => !c.defaultVisible && !c.alwaysVisible);

const formatEventDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const LINE_ITEMS_CONFIG = {
  pageTitle: 'Line Items',
  pageSubtitle: 'Financial line item entries across your venues.',
  sidebarKey: 'finances_line_items',
  defaultPreset: 'year_to_date',
  dateModeOptions: [
    { key: 'event_date', label: 'Event Date' },
  ],

  columns: LINE_ITEMS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingFinancesLineItems_${teamId}`,
  firstColumnId: 'event_id',
  defaultSortRules: [{ field: 'event_date', direction: 'desc' }],

  kpiFromDetailResponse: false,
  salesDataKey: null,
  venueSeriesKey: null,
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/line-items/export`,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/line-items/data.json`,
  detailResponseKey: 'line_items',
  detailErrorMessage: 'Failed to load line items data.',
  detailTableTitle: 'Line Items',
  emptyMessage: 'No line items found.',
  searchPlaceholder: 'Search line items...',
  paginationLabel: 'line items',
  itemLabel: (count) => `line item${count !== 1 ? 's' : ''}`,

  buildKpiCards: () => [],
  kpiGridColumns: 0,

  overTimeKey: null,
  salesCardTitle: null,
  chartColor: 'var(--sl-fg-brand, #1982c4)',

  topCharts: [],

  groupByOptions: null,
  groupByDefault: null,

  renderCellValue: (col, row) => {
    switch (col.id) {
      case 'event_id':
        return row.event_id || '\u2014';
      case 'event_date':
        return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '\u2014'}</span>;
      case 'event_name':
        return row.event_name || '\u2014';
      case 'venue_name':
        return row.venue_name || '\u2014';
      case 'finance_name':
        return row.finance_name || '\u2014';
      case 'estimate':
        return currencyFormatter.format(row.estimate || 0);
      case 'actual':
        return currencyFormatter.format(row.actual || 0);
      case 'paid':
        return row.paid === 'Paid'
          ? <span className="badge" style={{ background: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)', fontSize: 12 }}>Paid</span>
          : <span className="badge" style={{ background: 'var(--sl-bg-error-primary, #fef3f2)', color: 'var(--sl-text-error, #b42318)', fontSize: 12 }}>Not Paid</span>;
      case 'type':
        return row.type === 'Income'
          ? <span className="badge" style={{ background: 'var(--sl-bg-brand-primary, #eff8ff)', color: 'var(--sl-fg-brand, #175cd3)', fontSize: 12 }}>Income</span>
          : <span className="badge" style={{ background: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)', fontSize: 12 }}>Expense</span>;
      default:
        return '\u2014';
    }
  },

  filterTitle: 'Filter Line Items',
  filterSubtitle: 'Apply filters to narrow line item data.',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
    params.venue_name = activeFilters.venue_name;
  }
  if (activeFilters.event_name && activeFilters.event_name.length > 0) {
    params.event_name = activeFilters.event_name;
  }
  if (activeFilters.finance_category && activeFilters.finance_category.length > 0) {
    params.finance_category = activeFilters.finance_category;
  }
  if (activeFilters.paid && activeFilters.paid.length > 0) {
    params.paid = activeFilters.paid;
  }
  if (activeFilters.type && activeFilters.type.length > 0) {
    params.type = activeFilters.type;
  }
  return params;
};

function ReportingFinancesLineItemsPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/finances/line-items/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => {});
  }, [team && team.id, csrfToken]);

  const configWithFilters = {
    ...LINE_ITEMS_CONFIG,
    buildFilterGroups: () => buildLineItemsFilterGroups(filterOptions),
    buildDetailApiFilters,
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingFinancesLineItemsPage;
